export enum ErrorType {
  CONFLICT = 'conflict',
  UNAUTHORIZED = 'unauthorized',
  UNPROCESSABLE_ENTITY = 'unprocessable-entity',
  UNKNOWN = 'unknown',
  BAD_REQUEST = 'bad-request',
  FORBIDDEN = 'forbidden',
  SERVICE_UNAVAILABLE = 'service-unavailable',
}

class AppError extends Error {
  #type!: ErrorType;
  readonly code: string;
  readonly status?: number;
  readonly errorId?: string;
  readonly requestId?: string;
  readonly retryAfterMs?: number;

  static messages = {
    NOT_FOUND: 'O item requisitado não foi encontrado.',
    UNEXPECTED: 'Um erro inesperado ocorreu. Por favor, contate o suporte.',
    UNAVAILABLE_SERVICE:
      'Não foi possível conectar aos nossos servidores. Por favor, tente novamente mais tarde.',
    UNAUTHORIZED: 'unauthorized',
    FORBIDDEN: 'forbidden',
  };

  constructor(
    message: string,
    code: string = 'unknown',
    status?: number,
    errorId?: string,
    requestId?: string,
    retryAfterMs?: number,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.message = message;
    this.errorId = errorId;
    this.requestId = requestId;
    this.retryAfterMs = retryAfterMs;
    this.#type = ErrorType.UNKNOWN;
    this.name = 'AppError';
  }

  get type(): ErrorType {
    return this.#type;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  static rateLimitMessage(retryAfterMs?: number): string {
    const seconds = Math.ceil(
      retryAfterMs !== undefined &&
        Number.isFinite(retryAfterMs) &&
        retryAfterMs > 0
        ? retryAfterMs / 1000
        : 60,
    );

    const amount = seconds >= 60 ? Math.ceil(seconds / 60) : seconds;
    const unit =
      seconds >= 60
        ? amount === 1
          ? 'minuto'
          : 'minutos'
        : amount === 1
          ? 'segundo'
          : 'segundos';

    return `Você excedeu o limite de tentativas para esta ação. Tente novamente em ${amount} ${unit}.`;
  }

  get isConflict(): boolean {
    return this.#type === ErrorType.CONFLICT;
  }

  get isUnauthorized(): boolean {
    return this.#type === ErrorType.UNAUTHORIZED;
  }

  get isForbidden(): boolean {
    return this.#type === ErrorType.FORBIDDEN;
  }

  get isAccountVerificationRequired(): boolean {
    return this.code === 'account-verification-required';
  }

  get isUnprocessableEntity(): boolean {
    return this.#type === ErrorType.UNPROCESSABLE_ENTITY;
  }

  get isUnknown(): boolean {
    return this.#type === ErrorType.UNKNOWN;
  }

  get isBadRequest(): boolean {
    return this.#type === ErrorType.BAD_REQUEST;
  }

  get isServiceUnavailable(): boolean {
    return this.#type === ErrorType.SERVICE_UNAVAILABLE;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  static BadRequest(message: string): AppError {
    const error = new AppError(message);

    error.#type = ErrorType.BAD_REQUEST;
    return error;
  }

  static Conflict(message: string): AppError {
    const error = new AppError(message);

    error.#type = ErrorType.CONFLICT;
    return error;
  }

  static Unauthorized(): AppError {
    const error = new AppError(AppError.messages.UNAUTHORIZED);

    error.#type = ErrorType.UNAUTHORIZED;
    return error;
  }

  static Forbidden(): AppError {
    const error = new AppError(AppError.messages.FORBIDDEN);

    error.#type = ErrorType.FORBIDDEN;
    return error;
  }

  static UnprocessableEntity(message: string): AppError {
    const error = new AppError(message);

    error.#type = ErrorType.UNPROCESSABLE_ENTITY;
    return error;
  }

  static Unknown(message: string = AppError.messages.UNEXPECTED): AppError {
    const error = new AppError(message);

    error.#type = ErrorType.UNKNOWN;
    return error;
  }

  static ServiceUnavailable(): AppError {
    const error = new AppError(
      AppError.messages.UNAVAILABLE_SERVICE,
      'service-unavailable',
    );

    error.#type = ErrorType.SERVICE_UNAVAILABLE;
    return error;
  }

  static fromResponse(
    status: number,
    code: string,
    message?: string,
    errorId?: string,
    requestId?: string,
    retryAfterMs?: number,
  ): AppError {
    const error = new AppError(
      status === 429
        ? AppError.rateLimitMessage(retryAfterMs)
        : (message ?? code),
      code,
      status,
      errorId,
      requestId,
      retryAfterMs,
    );

    if (status === 400) {
      error.#type = ErrorType.BAD_REQUEST;
    } else if (status === 401) {
      error.#type = ErrorType.UNAUTHORIZED;
    } else if (status === 403) {
      error.#type = ErrorType.FORBIDDEN;
    } else if (status === 409) {
      error.#type = ErrorType.CONFLICT;
    } else if (status === 422) {
      error.#type = ErrorType.UNPROCESSABLE_ENTITY;
    } else if ([502, 503, 504].includes(status)) {
      error.#type = ErrorType.SERVICE_UNAVAILABLE;
    } else {
      error.#type = ErrorType.UNKNOWN;
    }

    return error;
  }
}

export default AppError;
